import { html, LitElement, nothing, type PropertyValueMap, unsafeCSS } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import type { LobbyGame } from '../../_ui-core_/base/package/api/package/gaming/gaming-domain';
import { panicModeInitiated, sendUserConfirmation } from '../../_ui-core_/base/package/api/package/pam/pam-api';
import { SubHelper } from '../../_ui-core_/base/package/patterns/package/store';
import { updateMetaTitle } from '../../_ui-core_/base/package/util/html-helper';
import { UIConfirmLocationModal } from '../../_ui-core_/components/drawers/confirm-location-modal/ui-confirm-location-modal';
import { UIGenericErrorModal } from '../../_ui-core_/components/drawers/generic-error-modal/ui-generic-error-modal';
import { UILoginModal } from '../../_ui-core_/components/drawers/login-modal/ui-login-modal';
import { UIPanicInfoModal } from '../../_ui-core_/components/drawers/panic-info-modal/ui-panic-info-modal';
import { UIPanicModal } from '../../_ui-core_/components/drawers/panic-modal/ui-panic-modal';
import { BonusStage, BonusState } from '../../_ui-core_/mod-bonus/types';
import type {
  ContributionTarget,
  ContributionTournamentTarget,
  IncentiveTournament,
  MissionState,
  MissionsBundleType,
  TournamentState,
} from '../../_ui-core_/mod-incentive/types';
import App from '../../app';
import {
  ComplianceActionTypes,
  UserStates,
} from '../../app/package/base/service/activation-flow/activation-flow-domain';
import { LoginStatus } from '../../app/package/base/service/login/login-domain';
import { ModalType } from '../../app/package/base/service/modals/modal-service';
import { Layout } from '../../app/package/base/types';
import { REPORT_4XX__RETRY_REPORT_500 } from '../../constants';
import { LogOutReason } from '../logout/page';
import style from './styles.css?inline';
import './variants/game-page-desktop-fullscreen';
import './variants/game-page-mobile';

const CName = 'game-page';

@customElement(CName)
export class GamePage extends LitElement {
  static readonly styles = unsafeCSS(style);

  @property({ type: Boolean }) forReal: boolean;
  @property({ type: String }) gameId: string;
  @property({ type: String }) gameSlug: string;
  @property({ type: String }) studio: string;
  @property({ type: String }) layout: Layout;

  @state() private _loginStatus: LoginStatus;
  @state() private _game: LobbyGame;
  @state() private _contributionMissionTarget: ContributionTarget | null;
  @state() private _contributionTournamentTarget: ContributionTournamentTarget | null;
  @state() private _contributionTargetMissionState?: MissionState;
  @state() private _contributionTargetTournamentState?: TournamentState;
  @state() private _bundlesInProgress: MissionsBundleType[] | null = null;
  @state() private _tournamentsInProgress: IncentiveTournament[] | null = null;
  @state() private _locationConfirmed = false;

  private _isIncentiveAvailable = false;
  private _panicModeInProgress = false;

  // Disable for now as the popup is covered by the game view on iOS native.
  // TODO: fix iOS native handling of popup(s)
  private _promptShown = true;
  private _modal: UIConfirmLocationModal | undefined;

  private readonly _subHelper = new SubHelper();
  private readonly _promptLoginTimeoutMs = 60_000;
  private _promptLoginTimeout: ReturnType<typeof setTimeout>;

  private _updateBundlesInProgress = async (bundles: MissionsBundleType[] | null) => {
    this._bundlesInProgress = bundles;
    await this._inGameMissionProgress();
  };

  private _updateTournamentsInProgress = async (tournaments: IncentiveTournament[] | null) => {
    this._tournamentsInProgress = tournaments;
    await this._inGameMissionProgress();
  };

  private _updateMissionState = (missionState: MissionState | undefined) => {
    this._contributionTargetMissionState = missionState;
  };

  private _updateTournamentState = (tournamentState: TournamentState | undefined) => {
    this._contributionTargetTournamentState = tournamentState;
    window.$app.logger.log('Incentive: tournament state updated:', this._contributionTargetTournamentState);
  };

  async connectedCallback() {
    super.connectedCallback();

    this._loginStatus = await App.instance.login.getFinalLoginStatus();
    await App.instance.activationFlow.runActivationFlow();

    // Check if user is allowed to play. Redirect to home if not.
    const isCompliant = App.instance.activationFlow.isActionEnabled(ComplianceActionTypes.GAMEPLAY);
    if (!isCompliant) {
      App.instance.router.navigateToHome();
      return;
    }

    if (App.instance.activationFlow.getCurrentState() === UserStates.IDLE) {
      App.instance.activationFlow
        .waitForActivation()
        .then(async () => {
          await App.instance.activationFlow.checkUserActivation();
          const isGameplayCompliant = App.instance.activationFlow.isActionEnabled(ComplianceActionTypes.GAMEPLAY);
          if (!isGameplayCompliant) {
            App.instance.router.navigateToHome();
          }
        })
        .catch(() => {
          // Session expired or user not authorized — activation flow handles redirect
        });
    }

    if (window.$app.appConfig.gamanzaClientId.length && window.$app.incentiveApi) {
      this._isIncentiveAvailable = true;

      window.$app.incentiveApi.getBundlesInProgress().then(() => {
        if (!this.isConnected) return;
        this._subscribeToBundlesInProgress();
        this._subscribeToIncentiveMissionProgressUpdates();
      });

      window.$app.incentiveApi.getActiveTournaments().then(() => {
        if (!this.isConnected) return;
        this._subscribeToActiveTournaments();
        this._subscribeToIncentiveTournamentProgressUpdates();
      });
    }

    this._subHelper.addSub(
      App.instance.visibilityStore,
      (hidden) => (!hidden && !this._promptShown ? this._setupPromptTimeout() : this._disablePromptTimeout()),
      true,
    );
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this._subHelper.unsubscribeAll();
    App.instance.native.sendLandscapeEnable(false);
    this._disablePromptTimeout();
    App.instance.activationFlowModalService.setCurrentGameId();
    if (this._modal) {
      this._modal = undefined;
    }
  }

  protected firstUpdated(): void {
    window.$app.renderService.removePageTemplateTask();
  }

  async updated(_changedProperties: PropertyValueMap<any> | Map<PropertyKey, unknown>): Promise<void> {
    if (_changedProperties.has('gameId')) {
      App.instance.activationFlowModalService.setCurrentGameId(this.gameId);
      App.instance.content.getGame(this.gameId).then((game) => {
        this._game = { ...game };
        updateMetaTitle(game.title ?? window.$app.appConfig.metaTitle);
      });
      // based on current requirements, confirm location modal is only shown for SH where gamification does not exist,
      // so we either show the location confirm modal (SH) or proceed with this._inGameMissionProgress (Games)
      if (App.instance.appSettings.shouldShowLocationModal && !this._locationConfirmed) {
        this._showConfirmLocationModal();
      } else if (this._isIncentiveAvailable) {
        await this._inGameMissionProgress();
      }
    }
  }

  render() {
    if (
      this._game === undefined ||
      (App.instance.appSettings.shouldShowLocationModal &&
        this._loginStatus === LoginStatus.LOGGED_IN &&
        !this._locationConfirmed)
    ) {
      return nothing;
    }
    return html`
      <header>
        <ui-a11y-title>${this._game.title}</ui-a11y-title>
      </header>
      ${this.layout === Layout.Mobile ? this._renderMobileUi() : this._renderDesktopUi()}
    `;
  }

  private _renderDesktopUi() {
    window.$app.logger.log('render desktop UI full screen');
    return html`
      <game-page-desktop-fullscreen
        class="game-page"
        .forReal=${this.forReal}
        .gameId=${this.gameId}
        .gameName=${this._game.title}
        .gameSlug=${this.gameSlug}
        .incentiveMissionTarget=${this._contributionMissionTarget}
        .incentiveMissionState=${this._contributionTargetMissionState}
        .incentiveTournamentTarget=${this._contributionTournamentTarget}
        .incentiveTournamentState=${this._contributionTargetTournamentState}
        @missionComplete=${() => this._inGameMissionProgress(true)}
        @panicModeOn=${() => this._enablePanicMode()}
        @showDiscovery=${this._toggleDiscovery}
        @showPanicInfo=${this._showPanicInfo}
        @showQuickDeposit=${this._navigateToDeposit}
      ></game-page-desktop-fullscreen>
    `;
  }

  private _renderMobileUi() {
    window.$app.logger.log('render mobile UI');
    const isNativeIos = window.$app.device.isNativeAppIos;
    return html`
      <game-page-mobile
        class="game-page"
        .forReal=${this.forReal}
        .gameId=${this.gameId}
        .gameSlug=${this.gameSlug}
        .gameName=${this._game.title}
        .isNativeIos=${isNativeIos}
        .incentiveMissionTarget=${this._contributionMissionTarget}
        .incentiveMissionState=${this._contributionTargetMissionState}
        .incentiveTournamentTarget=${this._contributionTournamentTarget}
        .incentiveTournamentState=${this._contributionTargetTournamentState}
        @missionComplete=${() => this._inGameMissionProgress(true)}
        @panicModeOn=${() => this._enablePanicMode()}
        @showDiscovery=${this._toggleDiscovery}
        @showPanicInfo=${this._showPanicInfo}
        @showQuickDeposit=${this._navigateToDeposit}
      ></game-page-mobile>
    `;
  }

  private _showConfirmLocationModal() {
    if (this._modal) {
      window.$app.logger.log('confirm location modal already shown, skipping');
      return;
    }

    this._modal = new UIConfirmLocationModal();
    this._modal.onCloseCallback = () => {
      window.$app.vaix.trackLocationConfirm(false);
      App.instance.router.navigateToHome();
    };
    this._modal.onActionCallback = () => {
      this._sendLocationConfirmation().then((isSuccessful) => {
        if (isSuccessful) {
          window.$app.modal.closeCurrentType(ModalType.ConfirmLocationModal);
        }
      });
    };
    window.$app.logger.log('Showing confirm location modal');
    window.$app.modal.show(this._modal, ModalType.ConfirmLocationModal);
  }

  private async _sendLocationConfirmation(): Promise<boolean> {
    const jwt = window.$app.loginStore.value.jwt;
    if (!jwt) {
      window.$app.logger.log('No JWT available for location confirmation');
      this._redirectHomeAndShowErrorModal();
      return false;
    }
    try {
      await App.instance.http.call(
        window.$app.appConfig.apiUrl_pam,
        sendUserConfirmation(jwt, 'SH_LOCATION_CONFIRMATION'),
        REPORT_4XX__RETRY_REPORT_500,
      );
      this._locationConfirmed = true;
      return true;
    } catch (err) {
      window.$app.logger.log('sending location confirmation failed', err);
      this._redirectHomeAndShowErrorModal();
      return false;
    }
  }

  private _redirectHomeAndShowErrorModal() {
    App.instance.router.navigateToHome();
    window.$app.modal.closeCurrentType(ModalType.ConfirmLocationModal).then(() => {
      this._showErrorModal();
    });
  }

  private _setupPromptTimeout() {
    this._promptLoginTimeout = setTimeout(() => this._promptLoginPopup(), this._promptLoginTimeoutMs);
  }

  private _disablePromptTimeout() {
    clearTimeout(this._promptLoginTimeout);
  }

  private _promptLoginPopup() {
    if (this._loginStatus === LoginStatus.LOGGED_IN) {
      return;
    }
    this._promptShown = true;
    const modal = new UILoginModal();
    window.$app.modal.show(modal, ModalType.LoginModal);
  }

  private _navigateToDeposit() {
    window.$app.logger.log('redirect to deposit…');
    window.$app.product.gotoDeposit(false);
  }

  private _showPanicInfo(e: Event) {
    window.$app.logger.log('show panicInfo');
    const modal = new UIPanicInfoModal();
    window.$app.modal.show(modal, ModalType.PanicInfo, e.target as HTMLElement);
  }

  private async _toggleDiscovery(e: Event) {
    window.$app.logger.log('show discovery…');
    // Force-remove previous discovery modal to prevent DOM node retention (IGM-3644).
    await window.$app.modal.closeCurrentType(ModalType.GameDiscovery, true);
    const { UIGameDiscoveryModal } = await import(
      '../../_ui-core_/components/drawers/game-discovery-modal/ui-game-discovery-modal'
    );
    const modal = new UIGameDiscoveryModal();
    modal.game = this._game;
    window.$app.modal.show(modal, ModalType.GameDiscovery, e.target as HTMLElement);
  }

  private _showErrorModal() {
    const modal = new UIGenericErrorModal();
    window.$app.modal.show(modal, ModalType.GenericError);
  }

  private _showPanicModeModal() {
    window.$app.loginStore.next({ loginStatus: LoginStatus.LOGGED_OUT });
    const modal = new UIPanicModal();
    modal.action = () => App.instance.router.navigateToLogout(LogOutReason.PANIC, true);
    window.$app.modal.show(modal, ModalType.PanicMode);
  }

  private async _enablePanicMode() {
    if (this._panicModeInProgress) return;
    this._panicModeInProgress = true;
    const jwt = window.$app.loginStore.value.jwt;
    if (!jwt) {
      this._panicModeInProgress = false;
      return;
    }
    await App.instance.http
      .call(window.$app.appConfig.apiUrl_pam, panicModeInitiated(jwt), REPORT_4XX__RETRY_REPORT_500)
      .then(() => this._showPanicModeModal())
      .catch(() => {
        this._panicModeInProgress = false;
        this._showErrorModal();
      });
  }

  private async _inGameMissionProgress(forceBundleUpdate = false) {
    if (!this._isIncentiveAvailable || !this.isConnected) return;

    if (forceBundleUpdate) {
      const prevBundleId = this._contributionMissionTarget?.bundleId;
      // Snapshot bundle data before refresh — completed bundles disappear from the active list
      const prevBundle = prevBundleId ? this._bundlesInProgress?.find((b) => b.originalId === prevBundleId) : undefined;

      await window.$app.incentiveApi.getBundlesInProgress();
      await window.$app.incentiveApi.updatePendingRewards();
      if (!this.isConnected) return;

      if (prevBundle) {
        this._trackMultiStepMissionProgress(prevBundle);
      }
      this._trackFSAvailable();
    }

    const { mission, tournament } = await window.$app.incentiveApi.getInGameProgress(
      this.gameSlug,
      this.studio,
      this._bundlesInProgress,
      this._tournamentsInProgress,
    );
    if (!this.isConnected) return;

    this._contributionMissionTarget = mission;
    if (this._contributionMissionTarget) {
      window.$app.incentiveApi.subscribeToMissionProgressEvents(this._contributionMissionTarget);
      return;
    }

    this._contributionTournamentTarget = tournament;
    if (this._contributionTournamentTarget) {
      window.$app.logger.log('Incentive: tournament contributed target defined:', this._contributionTournamentTarget);
      window.$app.incentiveApi.subscribeToTournamentProgressEvents(this._contributionTournamentTarget);
      return;
    }

    this._resetInGameProgressMissionState();
    window.$app.incentiveApi.unsubscribeFromMissionProgressEvents();
    window.$app.incentiveApi.unsubscribeFromTournamentProgressEvents();
  }

  private _resetInGameProgressMissionState() {
    this._contributionMissionTarget = null;
    this._contributionTournamentTarget = null;
  }

  private _trackMultiStepMissionProgress(prevBundle: MissionsBundleType) {
    const bundle = this._bundlesInProgress?.find((b) => b.originalId === prevBundle.originalId) ?? prevBundle;
    if (!bundle.isMultiMission) return;

    const isStillActive = this._bundlesInProgress?.some((b) => b.originalId === bundle.originalId);
    const completedSteps = isStillActive ? bundle.completedMissionNumber : (prevBundle.completedMissionNumber ?? 0) + 1;

    if (!completedSteps) return;

    window.$app.track.gamificationGames({
      eventAction: `multi-step_mission_step${completedSteps}`,
      missionID: bundle.originalId,
      missionName: bundle.title,
    });
  }

  private _trackFSAvailable() {
    App.instance.bonus.getActualBonuses().then(() => {
      const bonusData = App.instance.bonus.bonusStore.value;
      if (!bonusData.actualBonuses) return;

      const freeSpinBonus = bonusData.actualBonuses.find(
        (b) => b.state === BonusState.ACTIVE && b.stage === BonusStage.FREE_SPIN_ACTIVE,
      );
      if (!freeSpinBonus) return;

      window.$app.track.gamificationGames({
        eventAction: 'FS_available',
        fsAvail: String(freeSpinBonus.bonusBalance.freeSpin?.count ?? 0),
      });
    });
  }

  private _subscribeToBundlesInProgress() {
    this._subHelper.addSub(window.$app.incentiveApi.signal.bundlesInProgress, this._updateBundlesInProgress, true);
  }

  private _subscribeToActiveTournaments() {
    this._subHelper.addSub(window.$app.incentiveApi.signal.activeTournaments, this._updateTournamentsInProgress, true);
  }

  private _subscribeToIncentiveMissionProgressUpdates() {
    this._subHelper.addSub(window.$app.incentiveApi.signal.realtimeMissionProgress, this._updateMissionState, true);
  }

  private _subscribeToIncentiveTournamentProgressUpdates() {
    this._subHelper.addSub(
      window.$app.incentiveApi.signal.realtimeTournamentProgress,
      this._updateTournamentState,
      true,
    );
  }
}

declare global {
  interface HTMLElementTagNameMap {
    [CName]: GamePage;
  }
}
